import type { Moment } from "moment";
import { taskService } from "@miplatform/api";
import { ATTEMPT_STATUSES, MAX_PAGE_SIZE } from "@miplatform/constants";
import {
  CalendarEventType,
  CalendarType,
  IApiResponse,
  IAttempt,
  ICalendarConfig,
  ICalendarSet,
  WorkOrderStatus,
} from "@miplatform/types";
import {
  buildSearchQuery,
  endOf,
  getGUIDArray,
  getOrFilter,
  startOf,
} from "@miplatform/utils";

const getDateRangeFilter = (
  range: { ge: Date; le: Date },
  calendarSetConfig: ICalendarSet
) => {
  const params: Record<string, any>[] = [
    { scheduledStartDate: range, startDate: null },
    { forcedNextOn: range, startDate: null },
    { startDate: range },
  ];

  if (calendarSetConfig.eventType !== CalendarEventType.WorkOrder) {
    params.unshift({
      displayDate: range,
    });
  }

  return getOrFilter(...params);
};
export const loadCalendarEvents = async ({
  calendarSetConfig,
  calendarConfig,
  startDate,
  endDate,
  userIds,
  search,
}: LoadCalendarEventsPayload): IApiResponse<IAttempt[]> => {
  const isWorkOrderCalendar =
    calendarSetConfig.eventType === CalendarEventType.WorkOrder;
  const getAttempts = isWorkOrderCalendar
    ? taskService.attempts.getAllWorkOrders
    : taskService.attempts.getAll;

  const templates = calendarConfig.workTemplateBases ?? [];

  const dateFilter = getDateRangeFilter(
    {
      ge: startOf("day", startDate).toDate(),
      le: endOf("day", endDate ?? startDate).toDate(),
    },
    calendarSetConfig
  );

  const assignableUserIds =
    userIds ?? calendarSetConfig.assignableUsers?.map((user) => user.id) ?? [];
  const userFilter =
    assignableUserIds.length > 0
      ? {
          assignedUserId: {
            in: getGUIDArray(assignableUserIds),
          },
        }
      : {};

  const searchQuery = Object.entries(
    buildSearchQuery(search, [
      "home/address/addressLine1",
      "home/communityName",
      "home/builderName",
      "home/address/city",
      "assignment/order/orderTemplateName",
    ])
  ).map(([key, value]) => ({
    [key]: value,
  }));

  return getAttempts({
    top: MAX_PAGE_SIZE,
    filters: {
      ...calendarConfig.filter,
      and: [
        ...((calendarConfig.filter?.and as Record<string, any>[]) || []),
        ...(calendarSetConfig.eventType === CalendarEventType.WorkOrder
          ? [{ workOrderStatus: { ne: WorkOrderStatus.Cancelled } }]
          : []),
        ...searchQuery,
      ],
      ...(calendarSetConfig.eventType === CalendarEventType.WorkOrder && {
        attemptStatus: { ne: ATTEMPT_STATUSES.CANCELLED },
      }),
      ...(calendarConfig.calendarType === CalendarType.HoldsList
        ? {
            isOrderFollowupDue: false,
            attemptStatus: { eq: ATTEMPT_STATUSES.HOLD },
          }
        : {
            ...userFilter,
            ...dateFilter,
          }),
      ...((templates.length > 0 ||
        calendarConfig.calendarType === CalendarType.HoldsList) && {
        assignment: {
          [calendarSetConfig.eventType]: {
            [`${calendarSetConfig.eventType}TemplateId`]: {
              in: getGUIDArray(templates.map(({ id }) => id)),
            },
            ...(calendarConfig.calendarType === CalendarType.HoldsList && {
              holdCalendarId: {
                type: "guid",
                value: calendarConfig.id,
              },
            }),
          },
        },
      }),
    },
    ...(calendarConfig.calendarType === CalendarType.HoldsList && {
      sort: "modifiedOn desc",
    }),
    params: {
      includeWorkOrders: !isWorkOrderCalendar,
    },
  });
};

type LoadCalendarEventsPayload = {
  calendarSetConfig: ICalendarSet;
  calendarConfig: ICalendarConfig;
  startDate: Moment;
  endDate?: Moment;
  userIds?: string[] | null;
  search?: string;
};
